import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IcSave, IcClose, IcArrowLeft, IcConciergeBell, IcCpu, IcFlower, IcGem, IcTag, IcPercent, IcClock, IcImage, IcTrash, IcAlertCircle } from '../../theme/icons';
import serviceManagementService from '../../services/serviceManagementService';
import Swal from '../../theme/swal';
import { CardSkeleton } from '../../components/Skeleton';

// Shared styling (matches AddCustomer / AddProduct across the app).
// Formulaire (Brand Kit §05) : champ .inp 42px bordé #D1D5DB rayon 6,
// 13.5px, padding 11, focus 2px encre ; libellé 12px 500 encre, mb 4 ;
// erreur = .inp-err (bordure 2px rouge, fond #FEF2F2) + message .form-err.
const inputClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const textareaClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const inputErrorClass = 'inp inp-err w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';

// Type de service — the three categories the owner asked for. Each carries a
// suggested duration (minutes) that pre-fills the "Durée" field when picked.
const SERVICE_TYPES = [
  { value: 'DIAGNOSTIC', label: 'Diagnostique', desc: 'Diagnostic Visage', icon: IcCpu, duration: 15 },
  { value: 'SOIN', label: 'Soin', desc: 'Soin Hydrafacial', icon: IcFlower, duration: 120 },
  { value: 'ACTE', label: 'Acte', desc: 'Piercing', icon: IcGem, duration: 15 },
];

// Human-readable minutes -> "15 min" / "2 h" / "1 h 30 min".
const formatDuration = (mins) => {
  const m = parseInt(mins, 10);
  if (!m || m <= 0) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} h ${r} min`;
  if (h) return `${h} h`;
  return `${m} min`;
};

// Mode panneau (owner 2026-09-08) : rendu dans une modale de la liste
// (Paramètres › Services) → `id` par prop, `onDone` / `onCancel` remplacent
// la navigation, en-tête Retour + chip masqué. Sans props = page /services/add.
const ServiceForm = ({ id: idProp, onDone, onCancel } = {}) => {
  const params = useParams();
  const id = idProp ?? params.id;
  const navigate = useNavigate();
  const embedded = Boolean(onDone || onCancel);
  const isEditMode = Boolean(id);
  // En panneau : sections séparées par un filet, sans cartes emboîtées.
  const cardClass = embedded ? 'pt-5 border-t border-gray-100 first:pt-0 first:border-t-0' : 'bg-white rounded-2xl border border-gray-200 p-6';

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'SOIN',
    duration: 120, // default = the SOIN suggestion
    description: '',
    price: '',
    remise: 0,
    is_active: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [validationErrors, setValidationErrors] = useState({});

  // Fetch service data if in edit mode
  useEffect(() => {
    const fetchServiceData = async () => {
      if (!isEditMode) return;

      try {
        setLoading(true);
        const serviceData = await serviceManagementService.getServiceById(id);

        setFormData({
          name: serviceData.name || '',
          type: serviceData.type || 'SOIN',
          duration: serviceData.duration ?? '',
          description: serviceData.description || '',
          price: serviceData.price || '',
          remise: serviceData.remise || 0,
          is_active: serviceData.is_active !== undefined ? serviceData.is_active : true,
        });
        if (serviceData.image) setImagePreview(serviceData.image);
      } catch (err) {
        console.error('Error fetching service:', err);
        setError('Erreur lors du chargement du service');
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger les informations du service',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchServiceData();
  }, [id, isEditMode]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({ ...prev, [name]: inputValue }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Picking a type pre-fills the suggested duration for that type.
  const handleTypeSelect = (t) => {
    setFormData((prev) => ({ ...prev, type: t.value, duration: t.duration }));
  };

  // Service picture upload (optional).
  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Le nom du service est requis';
    }

    if (!formData.price) {
      errors.price = 'Le prix est requis';
    } else if (isNaN(Number(formData.price)) || Number(formData.price) < 0) {
      errors.price = 'Le prix doit être un nombre positif';
    }

    if (formData.remise && (isNaN(Number(formData.remise)) || Number(formData.remise) < 0 || Number(formData.remise) > 100)) {
      errors.remise = 'La remise doit être entre 0 et 100%';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const durationVal =
        formData.duration === '' || formData.duration === null || formData.duration === undefined
          ? null
          : parseInt(formData.duration, 10);

      if (imageFile) {
        // Multipart — a new picture was selected.
        const fd = new FormData();
        fd.append('name', formData.name);
        fd.append('type', formData.type);
        fd.append('description', formData.description || '');
        fd.append('price', parseFloat(formData.price));
        fd.append('remise', parseFloat(formData.remise) || 0);
        fd.append('is_active', formData.is_active ? 'true' : 'false');
        if (durationVal !== null) fd.append('duration', durationVal);
        fd.append('image', imageFile);

        if (isEditMode) {
          await serviceManagementService.updateServiceForm(id, fd);
        } else {
          await serviceManagementService.createServiceForm(fd);
        }
      } else {
        const serviceData = {
          ...formData,
          duration: durationVal,
          price: parseFloat(formData.price),
          remise: parseFloat(formData.remise) || 0,
          // No new file: keep the existing image, or clear it if the user removed it.
          image: imagePreview ? undefined : null,
        };

        if (isEditMode) {
          await serviceManagementService.updateService(id, serviceData);
        } else {
          await serviceManagementService.createService(serviceData);
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: isEditMode ? 'Service mis à jour avec succès' : 'Service créé avec succès',
        timer: 1500,
      });

      if (onDone) onDone(); else navigate('/services');
    } catch (err) {
      console.error('Error saving service:', err);

      if (err.response && err.response.data) {
        const apiErrors = {};
        Object.entries(err.response.data).forEach(([key, value]) => {
          apiErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setValidationErrors(apiErrors);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: "Une erreur est survenue lors de l'enregistrement",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => (onCancel ? onCancel() : navigate('/services'));

  const finalPrice =
    formData.price && formData.remise > 0
      ? (parseFloat(formData.price) * (1 - parseFloat(formData.remise) / 100)).toFixed(3)
      : null;

  if (loading) {
    return (
      <div className={embedded ? 'flex justify-center items-center py-16' : 'flex justify-center items-center min-h-[60vh]'}>
        <div className="text-center">
          <CardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'p-6' : 'max-w-3xl mx-auto px-4 sm:px-6 pt-2 pb-4'}>
      {/* Header (page seule) */}
      {!embedded && (
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={handleCancel}
          className="btn-s h-10 w-10 transition"
          title="Retour"
        >
          <IcArrowLeft />
        </button>
        {/* Page name as a gray chip — same design as the AddProduct page
            (owner Studio); old gradient icon box + h1 + subtitle removed. */}
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-gray-100 border-gray-400 text-gray-500 shadow-sm">
          <IcConciergeBell size={13} />
          {isEditMode ? 'Modifier le service' : 'Ajouter un nouveau service'}
        </span>
      </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Card 1: Informations */}
        <div className={cardClass}>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Informations du service</h2>

          {/* Name */}
          <div className="mb-5">
            <label htmlFor="name" className={labelClass}>
              Nom du Service <span className="req">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={validationErrors.name ? inputErrorClass : inputClass}
              placeholder="Ex : Diagnostic Visage, Soin basique…"
            />
            {validationErrors.name && <p className="form-err"><IcAlertCircle size={12} /> {validationErrors.name}</p>}
          </div>

          {/* Image */}
          <div className="mb-5">
            <label className={labelClass}>Image du service</label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 shrink-0 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
                {imagePreview ? (
                  <img src={imagePreview} alt="Aperçu" className="h-full w-full object-cover" />
                ) : (
                  <IcImage className="text-gray-300 text-2xl" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition w-fit">
                  <IcImage /> {imagePreview ? "Changer l'image" : 'Choisir une image'}
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 w-fit"
                  >
                    <IcTrash /> Retirer
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Type de service — segmented selector */}
          <div className="mb-5">
            <label className={labelClass}>
              Type de service <span className="req">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {SERVICE_TYPES.map((t) => {
                const Icon = t.icon;
                const active = formData.type === t.value;
                return (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => handleTypeSelect(t)}
                    className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition ${
                      active
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span
                      className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <Icon />
                    </span>
                    <span className="font-semibold text-gray-800">{t.label}</span>
                    <span className="text-xs text-gray-500">{t.desc}</span>
                  </button>
                );
              })}
            </div>
            {validationErrors.type && <p className="form-err"><IcAlertCircle size={12} /> {validationErrors.type}</p>}
          </div>

          {/* Durée / timing */}
          <div className="mb-5">
            <label htmlFor="duration" className={labelClass}>Durée (minutes)</label>
            <div className="relative max-w-xs">
              <IcClock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                min="0"
                step="5"
                className={`${inputClass} pl-9`}
                placeholder="15"
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              {formatDuration(formData.duration)
                ? `Durée estimée : ${formatDuration(formData.duration)}.`
                : 'Durée estimée du service.'}{' '}
              Suggestion selon le type, modifiable.
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className={labelClass}>Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className={textareaClass}
              placeholder="Description détaillée du service"
            />
          </div>
        </div>

        {/* Card 2: Tarification */}
        <div className={cardClass}>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Tarification</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="price" className={labelClass}>
                Prix (DT) <span className="req">*</span>
              </label>
              <div className="relative">
                <IcTag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.001"
                  className={`${validationErrors.price ? inputErrorClass : inputClass} pl-9`}
                  placeholder="0.000"
                />
              </div>
              {validationErrors.price && <p className="form-err"><IcAlertCircle size={12} /> {validationErrors.price}</p>}
            </div>

            <div>
              <label htmlFor="remise" className={labelClass}>Remise (%)</label>
              <div className="relative">
                <IcPercent className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="number"
                  id="remise"
                  name="remise"
                  value={formData.remise}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className={`${validationErrors.remise ? inputErrorClass : inputClass} pl-9`}
                  placeholder="0"
                />
              </div>
              {validationErrors.remise && <p className="form-err"><IcAlertCircle size={12} /> {validationErrors.remise}</p>}
            </div>
          </div>

          {finalPrice && (
            <div className="mt-4 flex items-center justify-between rounded-xl bg-green-50 border border-green-100 px-4 py-3">
              <span className="text-sm text-gray-600">Prix après remise</span>
              <span className="text-lg font-bold text-green-600">{finalPrice} DT</span>
            </div>
          )}
        </div>

        {/* Card 3: Statut */}
        <div className={cardClass}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-800">Service actif</h2>
              <p className="text-sm text-gray-500">Un service inactif n'apparaît pas lors de la création d'une session.</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData((prev) => ({ ...prev, is_active: !prev.is_active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                formData.is_active ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-pressed={formData.is_active}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  formData.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Actions — pied de formulaire (Brand Kit §05) : filet gray-100, pt 12, gap 8 */}
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-g px-5 py-2.5 text-sm transition"
          >
            <IcClose /> Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-p px-5 py-2.5 text-sm transition"
          >
            <IcSave /> {submitting ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;
